import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/shared/SEOHead";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Signal, Smartphone, Headphones, CheckCircle, ArrowRight, Wifi, Shield, Zap, Wrench, Tablet, Home, Router, Package, Cpu, GraduationCap } from "lucide-react";
import { getPageContent } from "@/lib/api";

const ICON_MAP = { Signal, Smartphone, Headphones, Wifi, Shield, Zap, Wrench, Tablet, Home, Router };

const PLAN_CATEGORIES = [
  { key: "phone", label: "Phone Plans", icon: Smartphone },
  { key: "tablet", label: "Tablet Plans", icon: Tablet },
  { key: "home_internet", label: "Home Internet", icon: Home },
  { key: "hotspot", label: "Mobile Hotspot", icon: Router },
];

const PMW_CATEGORIES = [
  { key: "devices", label: "Devices", icon: Smartphone },
  { key: "accessories", label: "Accessories", icon: Package },
  { key: "electronic_parts", label: "Electronic Parts", icon: Cpu },
  { key: "training", label: "Training & Support", icon: GraduationCap },
];

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

export default function ServicesPage() {
  const [sections, setSections] = useState([]);
  const [planCategory, setPlanCategory] = useState("phone");
  const [pmwCategory, setPmwCategory] = useState("devices");
  useEffect(() => { getPageContent("services").then(setSections).catch(() => {}); }, []);

  const hero = getSection(sections, "hero", { title: "Everything You Need to Run a Successful Wireless Store", content: "From premium plans to the latest devices and accessories.", metadata: { subtitle: "Our Services" } });
  const plans = getSection(sections, "plans", { title: "Plans Your Customers Will Love", content: "Offer your customers flexible, no-contract plans.", metadata: { subtitle: "Total Wireless Plans", items: [], disclaimer: "" } });
  const pmwServices = getSection(sections, "prime_media_wireless_services", {});
  const network = getSection(sections, "network", { title: "Powered by America's Most Reliable 5G Network", content: "", image_url: "", metadata: { subtitle: "Verizon Network", items: [] } });
  const devices = getSection(sections, "devices", { title: "Devices & Accessories", content: "", metadata: { subtitle: "Products", items: [] } });
  const cta = getSection(sections, "cta", { title: "Ready to Offer These Services?", content: "Join Prime Media Wireless.", button_text: "Become a Dealer", button_link: "/become-dealer" });

  const planItems = plans.metadata?.items || [];
  const tabletPlanItems = plans.metadata?.tablet_items || [];
  const homeInternetItems = plans.metadata?.home_internet_items || [];
  const hotspotItems = plans.metadata?.hotspot_items || [];
  const networkItems = network.metadata?.items || [];
  const deviceItems = devices.metadata?.items || [];

  const getPlanItemsForCategory = (cat) => {
    switch (cat) {
      case "phone": return planItems;
      case "tablet": return tabletPlanItems;
      case "home_internet": return homeInternetItems;
      case "hotspot": return hotspotItems;
      default: return [];
    }
  };

  const getPmwItemsForCategory = (cat) => {
    const m = pmwServices.metadata || {};
    switch (cat) {
      case "devices": return m.devices_items || [];
      case "accessories": return m.accessories_items || [];
      case "electronic_parts": return m.electronic_parts_items || [];
      case "training": return m.training_items || [];
      default: return [];
    }
  };

  const activePlanItems = getPlanItemsForCategory(planCategory);
  const activePmwItems = getPmwItemsForCategory(pmwCategory);
  const hasAnyPlans = planItems.length > 0 || tabletPlanItems.length > 0 || homeInternetItems.length > 0 || hotspotItems.length > 0;
  const hasAnyPmw = (pmwServices.metadata?.devices_items || []).length > 0 || (pmwServices.metadata?.accessories_items || []).length > 0 || (pmwServices.metadata?.electronic_parts_items || []).length > 0 || (pmwServices.metadata?.training_items || []).length > 0;

  // Build dynamic middle sections sorted by order
  const middleSections = [
    { key: "plans", order: plans.order || 99, hasContent: hasAnyPlans },
    { key: "pmw", order: pmwServices.order || 99, hasContent: pmwServices.title && (hasAnyPmw || (pmwServices.metadata?.items || []).length > 0) },
    { key: "network", order: network.order || 99, hasContent: networkItems.length > 0 },
    { key: "devices", order: devices.order || 99, hasContent: deviceItems.length > 0 },
  ].filter(s => s.hasContent).sort((a, b) => a.order - b.order);

  const renderPlansSection = () => (
    <section key="plans" className="py-20 bg-slate-50" data-testid="services-plans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="label-style text-teal mb-2">{plans.metadata?.subtitle}</p>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-navy mb-4">{plans.title}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto font-body">{plans.content}</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="plan-category-tabs">
          {PLAN_CATEGORIES.map((cat) => {
            const items = getPlanItemsForCategory(cat.key);
            if (items.length === 0) return null;
            const CatIcon = cat.icon;
            return (
              <button key={cat.key} onClick={() => setPlanCategory(cat.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body font-medium transition-all ${
                  planCategory === cat.key ? "bg-navy text-white shadow-lg" : "bg-white text-slate-600 border border-slate-200 hover:border-navy hover:text-navy"
                }`} data-testid={`plan-tab-${cat.key}`}>
                <CatIcon className="w-4 h-4" />{cat.label}
              </button>
            );
          })}
        </div>
        <div className={`grid gap-6 ${activePlanItems.length === 1 ? "max-w-md mx-auto" : activePlanItems.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
          {activePlanItems.map((plan, index) => (
            <motion.div key={`${planCategory}-${plan.name}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-xl border ${plan.popular ? "border-teal ring-2 ring-teal" : "border-slate-200"} p-6 relative`}>
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-teal text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span></div>}
              <div className="text-center mb-6">
                <h3 className="font-heading font-semibold text-navy text-xl mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1"><span className="font-heading font-bold text-4xl text-navy">{plan.price}</span><span className="text-slate-500 font-body">/mo</span></div>
                {plan.data && <p className="text-teal font-semibold mt-1">{plan.data.startsWith("$") ? plan.data : `${plan.data} Data`}</p>}
              </div>
              <ul className="space-y-3 mb-6">
                {(plan.features || []).map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-slate-600 font-body"><CheckCircle className="w-4 h-4 text-teal flex-shrink-0" />{feature}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        {plans.metadata?.disclaimer && <p className="text-center text-slate-500 text-sm mt-8 font-body">{plans.metadata.disclaimer}</p>}
      </div>
    </section>
  );

  const renderPmwSection = () => (
    <section key="pmw" className="py-20 bg-white" data-testid="services-pmw">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="label-style text-teal mb-2">{pmwServices.metadata?.subtitle || ""}</p>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-navy mb-4">{pmwServices.title}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto font-body">{pmwServices.content}</p>
        </div>
        {hasAnyPmw && (
          <>
            <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="pmw-category-tabs">
              {PMW_CATEGORIES.map((cat) => {
                const items = getPmwItemsForCategory(cat.key);
                if (items.length === 0) return null;
                const CatIcon = cat.icon;
                return (
                  <button key={cat.key} onClick={() => setPmwCategory(cat.key)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body font-medium transition-all ${
                      pmwCategory === cat.key ? "bg-navy text-white shadow-lg" : "bg-white text-slate-600 border border-slate-200 hover:border-navy hover:text-navy"
                    }`} data-testid={`pmw-tab-${cat.key}`}>
                    <CatIcon className="w-4 h-4" />{cat.label}
                  </button>
                );
              })}
            </div>
            <div className={`grid gap-6 ${activePmwItems.length === 1 ? "max-w-md mx-auto" : activePmwItems.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
              {activePmwItems.map((item, index) => (
                <motion.div key={`${pmwCategory}-${item.name}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-xl border ${item.popular ? "border-teal ring-2 ring-teal" : "border-slate-200"} p-6 relative shadow-sm`}>
                  {item.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-teal text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span></div>}
                  <div className="text-center mb-6">
                    <h3 className="font-heading font-semibold text-navy text-xl mb-2">{item.name}</h3>
                    {item.price && <div className="flex items-baseline justify-center gap-1"><span className="font-heading font-bold text-4xl text-navy">{item.price}</span>{item.price && <span className="text-slate-500 font-body">/mo</span>}</div>}
                    {item.data && <p className="text-teal font-semibold mt-1">{item.data}</p>}
                  </div>
                  <ul className="space-y-3 mb-6">
                    {(item.features || []).map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-slate-600 font-body"><CheckCircle className="w-4 h-4 text-teal flex-shrink-0" />{feature}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </>
        )}
        {!hasAnyPmw && (pmwServices.metadata?.items || []).length > 0 && (
          <div className={`grid gap-6 ${(pmwServices.metadata.items).length === 1 ? "max-w-md mx-auto" : (pmwServices.metadata.items).length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
            {(pmwServices.metadata.items).map((plan, index) => (
              <motion.div key={plan.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl border ${plan.popular ? "border-teal ring-2 ring-teal" : "border-slate-200"} p-6 relative shadow-sm`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-teal text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span></div>}
                <div className="text-center mb-6">
                  <h3 className="font-heading font-semibold text-navy text-xl mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1"><span className="font-heading font-bold text-4xl text-navy">{plan.price}</span><span className="text-slate-500 font-body">/mo</span></div>
                  {plan.data && <p className="text-teal font-semibold mt-1">{plan.data.startsWith("$") ? plan.data : `${plan.data} Data`}</p>}
                </div>
                <ul className="space-y-3 mb-6">
                  {(plan.features || []).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-600 font-body"><CheckCircle className="w-4 h-4 text-teal flex-shrink-0" />{feature}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        )}
        {pmwServices.metadata?.disclaimer && <p className="text-center text-slate-500 text-sm mt-8 font-body">{pmwServices.metadata.disclaimer}</p>}
      </div>
    </section>
  );

  const renderNetworkSection = () => (
    <section key="network" className="py-20 bg-white" data-testid="services-network">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="label-style text-teal mb-2">{network.metadata?.subtitle}</p>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-navy mb-6">{network.title}</h2>
            <p className="text-slate-600 mb-8 font-body">{network.content}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {networkItems.map((item) => {
                const Icon = ICON_MAP[item.icon] || Signal;
                return (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-teal" /></div>
                    <div><h4 className="font-heading font-semibold text-navy">{item.title}</h4><p className="text-slate-500 text-sm font-body">{item.description}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            {network.image_url && <img src={network.image_url} alt="5G Network" className="rounded-2xl shadow-xl" />}
          </motion.div>
        </div>
      </div>
    </section>
  );

  const renderDevicesSection = () => (
    <section key="devices" className="py-20 bg-navy" data-testid="services-devices">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="label-style text-teal mb-2">{devices.metadata?.subtitle}</p>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">{devices.title}</h2>
          <p className="text-slate-400 max-w-2xl mx-auto font-body">{devices.content}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {deviceItems.map((item, index) => {
            const Icon = ICON_MAP[item.icon] || Smartphone;
            return (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-xl">
                <div className="w-14 h-14 bg-teal rounded-xl flex items-center justify-center mb-6"><Icon className="w-7 h-7 text-navy" /></div>
                <h3 className="font-heading font-bold text-white text-2xl mb-4">{item.title}</h3>
                <p className="text-slate-400 mb-6 font-body">{item.description}</p>
                <ul className="space-y-2">
                  {(item.brands || []).map((brand) => (
                    <li key={brand} className="flex items-center gap-2 text-slate-300 font-body"><CheckCircle className="w-4 h-4 text-teal" />{brand}</li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );

  const sectionRenderers = {
    plans: renderPlansSection,
    pmw: renderPmwSection,
    network: renderNetworkSection,
    devices: renderDevicesSection,
  };

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="services"
        fallback={{
          meta_title: "Wireless Dealer Services | Devices, Plans, Accessories & Repair | Prime Media Wireless",
          meta_description: "Prime Media Wireless offers a complete wireless dealer product portfolio: smartphones, wireless plans, accessories, and electronic repair.",
          canonical_url: `${window.location.origin}/services`,
        }}
      />
      <section className="bg-navy py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
          </div>
        </div>
      </section>

      {/* Dynamic middle sections rendered by order */}
      {middleSections.map((s) => sectionRenderers[s.key]?.())}

      <section className="py-20 bg-teal" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">{cta.title}</h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8 font-body">{cta.content}</p>
          {cta.button_text && (
            <Link to={cta.button_link || "/become-dealer"}>
              <Button size="lg" className="bg-navy hover:bg-navy-dark text-white font-semibold px-8" data-testid="services-cta-btn">{cta.button_text}<ArrowRight className="w-5 h-5 ml-2" /></Button>
            </Link>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
